import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { X, CreditCard, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const PaymentModal = ({ isOpen, onClose, onSuccess, productName = "Full Purpose Profile", priceId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkoutInstance, setCheckoutInstance] = useState(null);
  const checkoutRef = useRef(null);

  // Demo Stripe configuration
  const STRIPE_PUBLIC_KEY = "pk_live_51Rngk9LLcWiUuGOnYEHu7Pt0HlCCXIaNOet7vWDRvpAzYgE1Naf7WW7ZnCQ6nvdQs0Fq0ypXpbvugiWYZYJpLD2k00Vzsx0U3V";
  const PRICE_ID = "price_1S79oRLLcWiUuGOnXX00XNqn";
  
  // Initialize Stripe
  const [stripe, setStripe] = useState(null);

  useEffect(() => {
    // Load Stripe when component mounts
    if (!stripe) {
      loadStripe(STRIPE_PUBLIC_KEY).then(setStripe);
    }
  }, [stripe]);

  useEffect(() => {
    if (isOpen && stripe) {
      initializeCheckout();
    }

    return () => {
      if (checkoutInstance) {
        checkoutInstance.destroy();
      }
    };
  }, [isOpen, stripe]);

  const initializeCheckout = async () => {
    if (!stripe) return;

    setIsLoading(true);
    setError(null);

    try {

      // Create checkout session
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: priceId || PRICE_ID,
          mode: 'payment',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { clientSecret } = await response.json();

      // Initialize embedded checkout
      const checkout = await stripe.initEmbeddedCheckout({
        clientSecret,
      });

      setCheckoutInstance(checkout);
      checkout.mount('#checkout-container');
      setIsLoading(false);

    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    if (checkoutInstance) {
      checkoutInstance.destroy();
      setCheckoutInstance(null);
    }
    initializeCheckout();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        {/* <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-medium text-gray-900">Complete Your Purchase</h2>
            <p className="text-gray-600 mt-1">Get your full Purpose Profile assessment</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div> */}



        {/* Checkout Container */}
        <div className="p-6 flex-1 overflow-y-auto">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-purple-600" size={32} />
              <span className="ml-3 text-gray-600">Loading checkout...</span>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="text-red-500 mr-2" size={20} />
                <div>
                  <p className="text-red-800 font-medium">Payment Error</p>
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              </div>
              <button
                onClick={handleRetry}
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          <div 
            id="checkout-container" 
            ref={checkoutRef}
            className="min-h-[400px]"
          >
            {/* {!isLoading && !error && (
              <div className="flex items-center justify-center py-12 text-gray-500">
                <CreditCard size={32} className="mr-3" />
                <span>Checkout loading...</span>
              </div>
            )} */}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-center text-sm text-gray-600">
            <CheckCircle size={16} className="mr-2 text-green-500" />
            Secure payment powered by Stripe
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
